<template>
  <div class="questions-card">
    <QuestionTabs
      v-if="tabbed"
      :tabs="tabs"
      :current="at"
      @select="at = $event"
    />

    <Question
      v-for="i in shown"
      :key="i"
      :question="questions[i]"
      :rows="rowsOf(questions[i])"
      :answer="answers[i]"
      :locked="locked"
      :marked="marked"
      :sendable="oneTap"
      @update="answers[i] = $event"
      @picked="picked"
      @send="sent"
    >
      <template #row="slotProps">
        <slot name="row" v-bind="slotProps"></slot>
      </template>
    </Question>

    <dl v-if="reviewing" class="questions-card__review">
      <div v-for="(question, i) in questions" :key="i">
        <dt>{{ question.header }}</dt>
        <dd v-if="isAnswered(selections[i])">
          {{ answerText([selections[i]]) }}
        </dd>
        <dd v-else class="questions-card__unanswered">
          {{ missed ? "Not answered" : "Not answered yet" }}
        </dd>
      </div>
    </dl>

    <button
      v-if="tabbed && !locked && !reviewing"
      type="button"
      class="questions-card__submit"
      :disabled="!isAnswered(selections[at])"
      @click="next"
    >
      Next
    </button>
    <button
      v-else-if="!locked && !oneTap"
      type="button"
      class="questions-card__submit"
      :disabled="!complete"
      @click="submit"
    >
      Submit
    </button>

    <!-- A tabbed card's review already says what was answered. -->
    <div v-if="summary && !tabbed" class="questions-card__status">
      <i class="fas fa-check" aria-hidden="true"></i>
      {{ summary }}
    </div>
    <div
      v-else-if="missed && !tabbed"
      class="questions-card__status questions-card__status--ignored"
    >
      <i class="fas fa-ban" aria-hidden="true"></i>
      Not answered
    </div>
  </div>
</template>

<script>
import Question from "./Question.vue";
import QuestionTabs from "./QuestionTabs.vue";
import {
  answerText,
  interactionResponse,
  isAnswered,
  selection,
} from "@/components/OliverBotV2/interactions/answers";

export default {
  name: "QuestionsCard",
  components: { Question, QuestionTabs },
  props: {
    /** The message's card: `[{id, type, questions}]`. */
    cards: { type: Array, required: true },
    /** `{selections}` when history shows the card was answered. */
    resolved: { type: Object, default: null },
    /** A later message exists, so an unanswered card can no longer be answered. */
    superseded: { type: Boolean, default: false },
    /** A question's rows. */
    rowsOf: { type: Function, required: true },
    /** What a picked row sends: a label, or who a person is. */
    valueOf: { type: Function, required: true },
    /** What makes a sent value the same pick again, to rebuild an answered card. */
    keyOf: { type: Function, required: true },
    /** Rows lead with a radio or a check box, unless they show their own mark. */
    marked: { type: Boolean, default: true },
  },
  emits: ["submit"],
  data() {
    const saved = this.resolved && this.resolved.selections;
    const questions = this.cards[0].questions;
    const closed = (saved && saved.length) || this.superseded;
    return {
      answers: questions.map((question, i) =>
        this.restored(question, saved && saved[i])
      ),
      summary: saved && saved.length ? answerText(saved) : "",
      // The open tab; past the last question is the review. A closed card opens on it.
      at: closed ? questions.length : 0,
    };
  },
  computed: {
    card() {
      return this.cards[0];
    },
    questions() {
      return this.card.questions;
    },
    /** One pick-one question: a tap is the whole answer. */
    oneTap() {
      return this.questions.length === 1 && !this.questions[0].multi_select;
    },
    /** Two or more questions: one at a time, then a review with Submit. */
    tabbed() {
      return this.questions.length > 1;
    },
    reviewing() {
      return this.tabbed && this.at === this.questions.length;
    },
    /** The questions on screen: all of them, or the open tab's. */
    shown() {
      if (!this.tabbed) return this.questions.map((_, i) => i);
      return this.reviewing ? [] : [this.at];
    },
    tabs() {
      return [
        ...this.questions.map((question, i) => ({
          label: question.header,
          done: isAnswered(this.selections[i]),
          missed: this.missed,
        })),
        { label: "Review", done: false, missed: false },
      ];
    },
    locked() {
      return Boolean(this.summary) || this.superseded;
    },
    /** A later message replaced the card before it was answered. */
    missed() {
      return this.superseded && !this.summary;
    },
    selections() {
      return this.questions.map((question, i) => {
        const { picked, otherOpen, other } = this.answers[i];
        const rows = this.rowsOf(question);
        const chosen = [...picked]
          .sort((a, b) => a - b)
          .map((p) => this.valueOf(rows[p]));
        return selection(question, chosen, otherOpen ? other : "");
      });
    },
    complete() {
      return this.selections.every(isAnswered);
    },
  },
  watch: {
    /** What was tapped and never sent never reached Oliver, so the card shows none of it. */
    missed(now) {
      if (!now) return;
      this.answers = this.questions.map((question) => this.restored(question));
      this.at = this.questions.length;
    },
  },
  methods: {
    answerText,
    isAnswered,
    /** A pick that answers its question: the whole answer, or on to the next tab. */
    picked() {
      if (this.oneTap) this.submit();
      else if (this.tabbed && isAnswered(this.selections[this.at])) this.next();
    },
    /** Typed words sent with Enter: the same as a pick. */
    sent() {
      this.picked();
    },
    next() {
      this.at = Math.min(this.at + 1, this.questions.length);
    },
    /** The answer state a saved selection puts back on its question. */
    restored(question, saved) {
      const keys = new Set(((saved && saved.chosen) || []).map(this.keyOf));
      const picked = this.rowsOf(question)
        .map((row, i) => (keys.has(this.keyOf(this.valueOf(row))) ? i : -1))
        .filter((i) => i >= 0);
      const other = (saved && saved.other) || "";
      return { picked, otherOpen: Boolean(other), other };
    },
    submit() {
      if (this.locked || !this.complete) return;
      const answers = this.selections;
      this.summary = answerText(answers);
      this.$emit("submit", {
        text: this.summary,
        response: interactionResponse(this.card, answers),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.questions-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 10px;
}

.questions-card__submit {
  align-self: flex-start;
  min-height: 44px;
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: var(--oliver-accent);
  color: #fff;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.questions-card__review {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 12px;
  margin: 0;
  font-size: 13px;
  line-height: 1.45;

  div {
    display: contents;
  }

  dt {
    color: var(--oliver-text-muted);
  }

  dd {
    min-width: 0;
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  .questions-card__unanswered {
    color: var(--oliver-text-muted);
    font-style: italic;
    font-weight: 400;
  }
}

.questions-card__status {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  line-height: 1.45;
  opacity: 0.75;
  overflow-wrap: anywhere;

  &--ignored {
    opacity: 0.6;
    font-style: italic;
  }
}
</style>
